<template>
  <div class="oi-gallery">
    <!-- Upload-Zone (Trello 21.08.): Drag&Drop UND Klick-Dateiauswahl, mehrere Dateien -->
    <div
      v-if="allowUpload"
      class="oi-drop"
      :class="{ 'oi-drop--over': dragOver }"
      @click="$refs.fileInput.click()"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <md-icon>add_photo_alternate</md-icon>
      <span>{{ $t("orders.images.dropHint") }}</span>
      <input ref="fileInput" type="file" accept="image/*" multiple style="display: none" @change="onFilesPicked" />
    </div>
    <p v-if="uploadError" class="oi-error">{{ $t("orders.images.uploadError") }}</p>

    <p v-if="!loading && !images.length" class="oi-empty">{{ $t("orders.images.empty") }}</p>
    <div v-if="loading" class="oi-loading"><md-progress-spinner md-mode="indeterminate" :md-diameter="30"></md-progress-spinner></div>

    <figure v-for="img in visibleImages" :key="img.id" class="oi-item">
      <img v-if="urls[img.id]" :src="urls[img.id]" alt="" @click="openViewer(img.id)" />
      <div v-else class="oi-item__loading"><md-progress-spinner md-mode="indeterminate" :md-diameter="24"></md-progress-spinner></div>
      <figcaption>
        {{ img.uploadedOn | formatDate }}
        <button v-if="allowUpload" class="oi-delete" :title="$t('orders.images.delete')" @click.stop="remove(img)">
          <md-icon>delete</md-icon>
        </button>
      </figcaption>
    </figure>

    <!-- Vorgemerkte Uploads (deferred, Karte 24.08.): erst "Speichern" laedt sie hoch -->
    <figure v-for="p in pendingUploads" :key="'pending-' + p.key" class="oi-item oi-item--pending">
      <img :src="p.url" alt="" />
      <figcaption>
        {{ $t("orders.images.pending") }}
        <button class="oi-delete" :title="$t('orders.images.delete')" @click.stop="removePending(p)">
          <md-icon>delete</md-icon>
        </button>
      </figcaption>
    </figure>

    <!-- Grossansicht wie im Retourentool: X, Blaettern - zusaetzlich Pfeiltasten und ESC -->
    <div v-if="viewerIndex !== null" class="oi-viewer" @click.self="closeViewer">
      <button class="oi-viewer__close" @click="closeViewer"><md-icon>close</md-icon></button>
      <button v-if="viewable.length > 1" class="oi-viewer__nav oi-viewer__nav--prev" @click.stop="stepViewer(-1)">
        <md-icon>chevron_left</md-icon>
      </button>
      <img :src="urls[viewable[viewerIndex].id]" alt="" />
      <button v-if="viewable.length > 1" class="oi-viewer__nav oi-viewer__nav--next" @click.stop="stepViewer(1)">
        <md-icon>chevron_right</md-icon>
      </button>
      <div class="oi-viewer__caption">{{ invoiceNumber }} ({{ viewerIndex + 1 }}/{{ viewable.length }})</div>
    </div>
  </div>
</template>

<script>
import OrdersService from "@/services/orders.service";
import { compressImage } from "@/helpers/imageCompress";

// Reklamationsbilder je RECHNUNGSNUMMER (Trello 21.08.). Laedt seine Liste selbst -
// die Komponente haengt an mehreren Stellen (Orders, Retouren-Liste, Erfassung) und
// braucht dort nur die Nummer. Bilder als Blob-URLs (Endpunkt ist authentifiziert).
export default {
  name: "OrderImageGallery",
  props: {
    invoiceNumber: { type: String, required: true },
    allowUpload: { type: Boolean, default: false },
    // deferred (Karte 24.08.): Uploads/Loeschungen werden nur vorgemerkt; der Parent ruft
    // commit() ("Speichern") oder discard() ("Abbrechen"/X). Ohne deferred wirkt alles sofort.
    deferred: { type: Boolean, default: false },
  },
  data() {
    return {
      images: [],
      urls: {},
      viewerIndex: null,
      loading: false,
      dragOver: false,
      uploadError: false,
      pendingUploads: [],
      pendingDeletes: [],
      pendingKey: 0,
    };
  },
  computed: {
    visibleImages() {
      return this.images.filter((i) => !this.pendingDeletes.includes(i.id));
    },
    viewable() {
      return this.visibleImages.filter((i) => this.urls[i.id]);
    },
  },
  watch: {
    invoiceNumber: { immediate: true, handler: "reload" },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    Object.values(this.urls).forEach((u) => URL.revokeObjectURL(u));
    this.pendingUploads.forEach((p) => URL.revokeObjectURL(p.url));
  },
  methods: {
    async reload() {
      if (!this.invoiceNumber) return;
      this.loading = true;
      try {
        this.images = await OrdersService.images(this.invoiceNumber);
        this.$emit("count", this.images.length);
        for (const img of this.images) {
          if (this.urls[img.id]) continue;
          const url = await OrdersService.imageContentUrl(this.invoiceNumber, img.id).catch(() => null);
          if (url) this.$set(this.urls, img.id, url);
        }
      } finally {
        this.loading = false;
      }
    },
    onDrop(e) {
      this.dragOver = false;
      this.uploadFiles([...(e.dataTransfer?.files || [])]);
    },
    onFilesPicked(e) {
      this.uploadFiles([...e.target.files]);
      e.target.value = "";
    },
    async uploadFiles(files) {
      this.uploadError = false;
      const bilder = files.filter((f) => f.type.startsWith("image/"));
      if (this.deferred) {
        // Nur vormerken (lokale Vorschau); Kompression + Upload erst in commit()
        for (const file of bilder) {
          this.pendingUploads.push({ key: ++this.pendingKey, file, url: URL.createObjectURL(file) });
        }
        return;
      }
      for (const file of bilder) {
        try {
          const compressed = await compressImage(file, { maxEdge: 2048, quality: 0.85 });
          await OrdersService.uploadImage(this.invoiceNumber, compressed);
        } catch (err) {
          this.uploadError = true;
        }
      }
      await this.reload();
    },
    async remove(img) {
      if (this.deferred) {
        if (!this.pendingDeletes.includes(img.id)) this.pendingDeletes.push(img.id);
        return;
      }
      try {
        await OrdersService.deleteImage(this.invoiceNumber, img.id);
      } finally {
        await this.reload();
      }
    },
    removePending(p) {
      URL.revokeObjectURL(p.url);
      this.pendingUploads = this.pendingUploads.filter((x) => x.key !== p.key);
    },
    /// Vorgemerkte Aenderungen ausfuehren ("Speichern"). Liefert false, wenn ein Upload scheiterte.
    async commit() {
      this.uploadError = false;
      let ok = true;
      for (const p of this.pendingUploads) {
        try {
          const compressed = await compressImage(p.file, { maxEdge: 2048, quality: 0.85 });
          await OrdersService.uploadImage(this.invoiceNumber, compressed);
          URL.revokeObjectURL(p.url);
        } catch (err) {
          this.uploadError = true;
          ok = false;
        }
      }
      for (const id of this.pendingDeletes) {
        try {
          await OrdersService.deleteImage(this.invoiceNumber, id);
        } catch (err) {
          ok = false;
        }
      }
      this.pendingUploads = [];
      this.pendingDeletes = [];
      await this.reload();
      return ok;
    },
    /// Vorgemerkte Aenderungen verwerfen ("Abbrechen"/X) - serverseitig passiert nichts.
    discard() {
      this.pendingUploads.forEach((p) => URL.revokeObjectURL(p.url));
      this.pendingUploads = [];
      this.pendingDeletes = [];
    },
    openViewer(id) {
      const idx = this.viewable.findIndex((i) => i.id === id);
      if (idx >= 0) this.viewerIndex = idx;
    },
    closeViewer() {
      this.viewerIndex = null;
    },
    stepViewer(delta) {
      const n = this.viewable.length;
      if (!n) return;
      this.viewerIndex = (this.viewerIndex + delta + n) % n;
    },
    onKeydown(e) {
      if (this.viewerIndex === null) return;
      if (e.key === "Escape") this.closeViewer();
      else if (e.key === "ArrowLeft") this.stepViewer(-1);
      else if (e.key === "ArrowRight") this.stepViewer(1);
    },
  },
};
</script>

<style lang="scss" scoped>
.oi-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.oi-empty {
  color: #9e9e9e;
  font-size: 13px;
}
.oi-error {
  width: 100%;
  color: #f44336;
  font-size: 13px;
  margin: 0;
}
.oi-loading {
  padding: 12px;
}
.oi-drop {
  width: 100%;
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #757575;
  cursor: pointer;

  &--over {
    border-color: #7f915a;
    background: #f2f6eb;
  }
}
.oi-item {
  margin: 0;
  width: 140px;

  &--pending img {
    outline: 2px dashed #7f915a;
    outline-offset: -2px;
  }

  img {
    width: 140px;
    height: 110px;
    object-fit: cover;
    border-radius: 6px;
    cursor: zoom-in;
    display: block;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  }

  figcaption {
    font-size: 12px;
    color: #616161;
    padding-top: 4px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  &__loading {
    width: 140px;
    height: 110px;
    border-radius: 6px;
    background: #fafafa;
    border: 1px dashed #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.oi-delete {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .md-icon {
    font-size: 16px !important;
    color: #b71c1c;
  }
}
.oi-viewer {
  position: fixed;
  inset: 0;
  z-index: 10002;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: calc(100vw - 140px);
    max-height: calc(100vh - 80px);
    object-fit: contain;
    border-radius: 4px;
  }

  &__close,
  &__nav {
    position: absolute;
    border: none;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__close {
    top: 14px;
    right: 14px;
  }

  &__nav {
    top: 50%;
    transform: translateY(-50%);

    &--prev {
      left: 14px;
    }
    &--next {
      right: 14px;
    }
  }

  &__caption {
    position: absolute;
    bottom: 12px;
    left: 0;
    right: 0;
    text-align: center;
    color: #fff;
    font-size: 13px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  }
}
</style>
